import { useEffect, useRef, useState } from "react";
import { Calendar, CaretDown, CaretLeft, CaretRight, X } from "@phosphor-icons/react";

/**
 * Reusable date-range filter used by Invoices + Repair Tickets + Bookings.
 * Presets: all, today, yesterday, last7, last30, custom
 * onChange({ mode, from, to })
 *   - from/to are JS Date objects (local), inclusive range start (from 00:00) and exclusive end (to next day 00:00)
 *   - mode === 'all' → from=null, to=null (no filter)
 */
export default function DateRangeFilter({ value, onChange }) {
  const { mode = "all", from, to } = value || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const apply = (m) => {
    const now = new Date();
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    let nextFrom = null;
    let nextTo = null;

    if (m === "today") {
      nextFrom = startOfDay(now);
      nextTo = new Date(nextFrom.getTime() + 24 * 60 * 60 * 1000);
    } else if (m === "yesterday") {
      const y = new Date(startOfDay(now).getTime() - 24 * 60 * 60 * 1000);
      nextFrom = y;
      nextTo = new Date(y.getTime() + 24 * 60 * 60 * 1000);
    } else if (m === "last7") {
      const end = new Date(startOfDay(now).getTime() + 24 * 60 * 60 * 1000);
      nextFrom = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      nextTo = end;
    } else if (m === "last30") {
      const end = new Date(startOfDay(now).getTime() + 24 * 60 * 60 * 1000);
      nextFrom = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
      nextTo = end;
    } else if (m === "month_to_date") {
      nextFrom = new Date(now.getFullYear(), now.getMonth(), 1);
      nextTo = new Date(startOfDay(now).getTime() + 24 * 60 * 60 * 1000);
    } else if (m === "last_month") {
      nextFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      nextTo = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (m === "year_to_date") {
      nextFrom = new Date(now.getFullYear(), 0, 1);
      nextTo = new Date(startOfDay(now).getTime() + 24 * 60 * 60 * 1000);
    }
    onChange({ mode: m, from: nextFrom, to: nextTo });
    if (m !== "custom") setOpen(false);
  };

  const applyCustom = (fromStr, toStr) => {
    if (!fromStr || !toStr) return;
    const fromD = new Date(fromStr + "T00:00:00");
    const toD = new Date(toStr + "T00:00:00");
    // Make `to` end-inclusive
    const toExclusive = new Date(toD.getTime() + 24 * 60 * 60 * 1000);
    onChange({ mode: "custom", from: fromD, to: toExclusive });
    setOpen(false);
  };

  // Also add quick preset rows for last-90 / this-month / this-year so the
  // premium calendar doesn't have to be opened for common ranges.
  const presetsExtra = [
    { id: "month_to_date", label: "Month to date" },
    { id: "last_month", label: "Last month" },
    { id: "year_to_date", label: "Year to date" },
  ];

  const presets = [
    { id: "all", label: "All time" },
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "last7", label: "Last 7 days" },
    { id: "last30", label: "Last 30 days" },
    ...presetsExtra,
  ];

  const pillLabel = (() => {
    const hit = presets.find((p) => p.id === mode);
    if (hit) return hit.label;
    if (mode === "custom" && from && to) {
      const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      const toDisplay = new Date(to.getTime() - 1);
      return `${fmt(from)} → ${fmt(toDisplay)}`;
    }
    return "All time";
  })();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="date-range-trigger"
        className={`h-11 px-4 rounded-full border text-[13px] font-medium flex items-center gap-2 transition-colors ${
          mode !== "all"
            ? "bg-[#121216] border-[#121216] text-white hover:bg-[#1E5FEB] hover:border-[#1E5FEB]"
            : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#121216] hover:text-[#121216]"
        }`}
      >
        <Calendar size={14} weight="bold" />
        <span>{pillLabel}</span>
        {mode !== "all" ? (
          <span
            role="button"
            aria-label="Clear date filter"
            onClick={(e) => { e.stopPropagation(); apply("all"); }}
            className="ml-1 -mr-1 w-5 h-5 rounded-full hover:bg-white/15 flex items-center justify-center"
            data-testid="date-range-clear"
          >
            <X size={10} weight="bold" />
          </span>
        ) : (
          <CaretDown size={10} weight="bold" className="opacity-70" />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-[#E5E7EB] z-40 overflow-hidden"
          style={{ boxShadow: "0 20px 48px rgba(30, 95, 235, 0.15)" }}
          data-testid="date-range-popover"
        >
          <ul className="py-1">
            {presets.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => apply(p.id)}
                  data-testid={`date-preset-${p.id}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition-colors ${
                    mode === p.id
                      ? "bg-[#121216] text-white"
                      : "text-[#4B5563] hover:bg-[#F8FAFC] hover:text-[#121216]"
                  }`}
                >
                  <span className="font-medium">{p.label}</span>
                  {mode === p.id && <span className="text-[11px] font-mono opacity-70">active</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-[#F3F4F6] p-4">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">
              Custom range
            </div>
            <CustomRange value={value} onApply={applyCustom} />
          </div>
        </div>
      )}
    </div>
  );
}

function CustomRange({ value, onApply }) {
  const initialFrom = value?.mode === "custom" && value.from ? new Date(value.from) : null;
  // value.to is exclusive — subtract 1ms for display so the picker highlights the right last day
  const initialTo = value?.mode === "custom" && value.to ? new Date(value.to.getTime() - 1) : null;
  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);

  // The calendar shows one month at a time. Start from the "from" date or today.
  const [viewMonth, setViewMonth] = useState(() => {
    const base = initialFrom || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  // Which field is being picked — clicking a day fills the active one. After
  // "from" is set we auto-swap to "to" so the user can tap both days back-to-back.
  const [activeField, setActiveField] = useState("from");

  const sameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const inRange = (d) => fromDate && toDate && d > fromDate && d < toDate;

  const pickDay = (d) => {
    // Simple two-click flow: first tap = from, second tap = to. If user taps
    // a day earlier than `from`, we swap them for them.
    if (activeField === "from" || !fromDate) {
      setFromDate(d);
      setActiveField("to");
      if (toDate && d > toDate) setToDate(null);
    } else {
      if (d < fromDate) {
        setToDate(fromDate);
        setFromDate(d);
      } else {
        setToDate(d);
      }
      setActiveField("from");
    }
  };

  const fmt = (d) =>
    d ? d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "Select";
  const toIso = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");

  return (
    <div className="space-y-3">
      {/* Selected dates — pill-shaped, premium look */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setActiveField("from")}
          data-testid="date-custom-from-pill"
          className={`h-11 px-3 rounded-xl border-2 text-left transition-all ${
            activeField === "from"
              ? "border-[#1E5FEB] bg-blue-50 ring-2 ring-blue-100"
              : "border-[#E5E7EB] bg-white hover:border-slate-300"
          }`}
        >
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">FROM</div>
          <div className={`text-[12px] font-bold ${fromDate ? "text-slate-900" : "text-slate-400"}`}>{fmt(fromDate)}</div>
        </button>
        <button
          type="button"
          onClick={() => setActiveField("to")}
          data-testid="date-custom-to-pill"
          className={`h-11 px-3 rounded-xl border-2 text-left transition-all ${
            activeField === "to"
              ? "border-[#1E5FEB] bg-blue-50 ring-2 ring-blue-100"
              : "border-[#E5E7EB] bg-white hover:border-slate-300"
          }`}
        >
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">TO</div>
          <div className={`text-[12px] font-bold ${toDate ? "text-slate-900" : "text-slate-400"}`}>{fmt(toDate)}</div>
        </button>
      </div>

      {/* Month / Year navigator */}
      <div className="flex items-center justify-between px-1">
        <button
          type="button"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          data-testid="cal-prev"
          className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"
        >
          <CaretLeft size={14} weight="bold" />
        </button>
        <div className="flex items-center gap-1.5">
          <select
            value={viewMonth.getMonth()}
            onChange={(e) => setViewMonth(new Date(viewMonth.getFullYear(), parseInt(e.target.value, 10), 1))}
            data-testid="cal-month"
            className="h-8 pr-2 pl-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
          >
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </select>
          <select
            value={viewMonth.getFullYear()}
            onChange={(e) => setViewMonth(new Date(parseInt(e.target.value, 10), viewMonth.getMonth(), 1))}
            data-testid="cal-year"
            className="h-8 pr-2 pl-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
          >
            {Array.from({ length: 40 }, (_, k) => new Date().getFullYear() + 2 - k).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
          data-testid="cal-next"
          className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"
        >
          <CaretRight size={14} weight="bold" />
        </button>
      </div>

      {/* Day grid */}
      <div>
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {["Mo","Tu","We","Th","Fr","Sa","Su"].map((d) => (
            <div key={d} className="text-[10px] font-bold text-slate-400 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {buildMonthDays(viewMonth).map((d, i) => {
            if (!d) return <div key={i} />;
            const isFrom = sameDay(d, fromDate);
            const isTo = sameDay(d, toDate);
            const isBetween = inRange(d);
            const isToday = sameDay(d, new Date());
            return (
              <button
                key={i}
                type="button"
                onClick={() => pickDay(d)}
                data-testid={`cal-day-${toIso(d)}`}
                className={`h-8 rounded-md text-[12px] font-mono tabular-nums transition-all relative ${
                  isFrom || isTo
                    ? "bg-[#1E5FEB] text-white font-bold shadow-sm"
                    : isBetween
                      ? "bg-blue-100 text-[#1E5FEB] font-semibold"
                      : isToday
                        ? "bg-slate-100 text-[#1E5FEB] font-bold ring-1 ring-[#1E5FEB]/30"
                        : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onApply(toIso(fromDate), toIso(toDate))}
        disabled={!fromDate || !toDate}
        data-testid="date-custom-apply"
        className="w-full h-10 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white text-[12px] font-bold transition-colors"
      >
        Apply range
      </button>
    </div>
  );
}

/** Returns a 6-row (42 cell) array: null pads for leading/trailing blanks.
 *  Week starts Monday. */
function buildMonthDays(viewMonth) {
  const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const last = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  // Monday-first offset: Sunday (0) → 6, Monday (1) → 0, ... Saturday (6) → 5
  const leadingPad = (first.getDay() + 6) % 7;
  const total = leadingPad + last.getDate();
  const cells = Array.from({ length: Math.ceil(total / 7) * 7 }, (_, i) => {
    const dayNum = i - leadingPad + 1;
    if (dayNum < 1 || dayNum > last.getDate()) return null;
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth(), dayNum);
  });
  return cells;
}

/**
 * Helper: returns true if `created_at` (ISO string) is within the provided range.
 * If mode === 'all' (no from/to), always true.
 */
export function isInRange(iso, range) {
  if (!range || range.mode === "all" || !range.from || !range.to) return true;
  if (!iso) return false;
  const ts = new Date(iso).getTime();
  if (isNaN(ts)) return false;
  return ts >= range.from.getTime() && ts < range.to.getTime();
}
